import * as React from "react";
import { Button, Chip, MenuItem, RadioGroup, FormControlLabel, Radio, Alert, Grid } from "@mui/material";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import Typography from "@mui/material/Typography";
import { useMemo, useState, useEffect } from "react";
import { TabPanelHeader } from "../../components/TabPanelHeader";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  IGameConfigBranchInfo,
  GameConfigState,
  useGameConfigService,
  ILatestReportsResponse,
  IDiffResponse,
  IStaticDataFileMapping,
} from "../../services/backend/GameConfigService";
import DeleteIcon from "@mui/icons-material/Delete";
import { BranchStateReport } from "./BranchStateReport";

export interface BranchStateTabPanelProps {
  branches: string[];
  branchInfo: IGameConfigBranchInfo | undefined;
  selectedBranch: string | undefined;
  setSelectedBranch: (branch: string) => void;
  createBranchButtonClickHandler: () => void;
  commitBranchButtonClickHandler: () => void;
  revertBranchButtonClickHandler: () => void;
  submitBranchButtonClickHandler: () => void;
  mergeBranchButtonClickHandler: () => void;
  deleteBranchButtonClickHandler: () => void;
  onSwitchToImportTab: () => void;
  onRemainingIdsUpdate: (remainingIds: IStaticDataFileMapping[] | undefined) => void;
  onImportOutputUpdate: (importOutput: string | undefined) => void;
  onDeployOutputUpdate: (deployOutput: string | undefined) => void;
}

export const BranchStateTabPanel = ({
  branches,
  selectedBranch,
  setSelectedBranch,
  branchInfo,
  createBranchButtonClickHandler,
  commitBranchButtonClickHandler,
  revertBranchButtonClickHandler,
  submitBranchButtonClickHandler,
  mergeBranchButtonClickHandler,
  deleteBranchButtonClickHandler,
  onSwitchToImportTab,
  onRemainingIdsUpdate,
  onImportOutputUpdate,
  onDeployOutputUpdate,
}: BranchStateTabPanelProps) => {
  const gameConfigService = useGameConfigService();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reports, setReports] = useState<ILatestReportsResponse | undefined>(undefined);
  const [diffData, setDiffData] = useState<IDiffResponse | undefined>(undefined);
  const [selectedReportView, setSelectedReportView] = useState<"import" | "deploy" | "legacy" | "changes">("import");

  const fetchReportsAndDiff = async () => {
    if (!selectedBranch) {
      setReports(undefined);
      setDiffData(undefined);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch both reports and diff data
      const [reportsResponse, diffResponse] = await Promise.all([
        gameConfigService.getLatestReports(selectedBranch),
        gameConfigService.getDiff(selectedBranch),
      ]);

      setReports(reportsResponse);
      setDiffData(diffResponse);
      onImportOutputUpdate(reportsResponse.importOutputLogFile.content ?? undefined);
      onDeployOutputUpdate(reportsResponse.deployOutputLogFile.content ?? undefined);
    } catch (error) {
      console.error("Failed to fetch reports and diff data:", error);
      setReports(undefined);
      setDiffData(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportsAndDiff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  const navigateToBranchOrPullRequest = (branchName: string | undefined, branchInfo: IGameConfigBranchInfo | undefined) => {
    if (branchInfo?.pullRequest) {
      window.open(`https://github.com/FortisGames/proj-townsfolk-game-platform/pull/${branchInfo.pullRequest.number}`);
    } else if (branchName) {
      window.open(`https://github.com/FortisGames/proj-townsfolk-game-platform/tree/${branchName}`);
    }
  };

  const branchChip = useMemo(() => {
    if (!branchInfo?.state) {
      return null;
    }

    switch (branchInfo.state) {
      case GameConfigState.DRAFT:
        return (
          <Chip
            label="Draft"
            variant="filled"
            size="small"
            color="warning"
            onClick={() => navigateToBranchOrPullRequest(selectedBranch, branchInfo)}
          />
        );
      case GameConfigState.IN_REVIEW:
        return (
          <Chip
            label="In review"
            variant="filled"
            size="small"
            color="warning"
            onClick={() => navigateToBranchOrPullRequest(selectedBranch, branchInfo)}
          />
        );
      case GameConfigState.APPROVED:
        return (
          <Chip
            label="Approved"
            variant="filled"
            size="small"
            color="success"
            onClick={() => navigateToBranchOrPullRequest(selectedBranch, branchInfo)}
          />
        );
      case GameConfigState.MERGED:
        return (
          <Chip
            label="Merged"
            variant="filled"
            size="small"
            color="success"
            onClick={() => navigateToBranchOrPullRequest(selectedBranch, branchInfo)}
          />
        );
      default:
        return (
          <Chip
            label="Unknown"
            variant="filled"
            size="small"
            color="warning"
            onClick={() => navigateToBranchOrPullRequest(selectedBranch, branchInfo)}
          />
        );
    }
  }, [selectedBranch, branchInfo]);

  const branchStatus = useMemo(() => {
    if (!branchInfo) {
      onRemainingIdsUpdate(undefined);
      return { type: "no-branch", message: "No branch selected", severity: "info" as const };
    }

    if (isLoading) {
      return { type: "loading", message: "Loading reports...", severity: "info" as const };
    }

    if (!reports) {
      onRemainingIdsUpdate(undefined);
      return { type: "error", message: "Error loading reports", severity: "error" as const };
    }

    // Check for remaining IDs that need attention
    if (reports.remainingIdsFile.exists && reports.remainingIdsFile.content) {
      try {
        const remainingIds = JSON.parse(reports.remainingIdsFile.content);
        if (Array.isArray(remainingIds) && remainingIds.length > 0) {
          onRemainingIdsUpdate(
            remainingIds.map((o) => {
              const t = { id: o["Id"] } as IStaticDataFileMapping;
              if (o["Database"]) {
                t.database = o["Database"];
              }
              if (o["CurrentSDId"]) {
                t.currentSDId = o["CurrentSDId"];
              }
              if (o["CurrentId"]) {
                t.currentId = o["CurrentId"];
              }
              return t;
            }),
          );
          return {
            type: "remaining-ids",
            message: `${remainingIds.length} unmapped items require attention`,
            severity: "warning" as const,
          };
        }
      } catch (_) {
        onRemainingIdsUpdate(undefined);
        return { type: "error", message: "Error parsing remaining IDs data", severity: "error" as const };
      }
    }

    // Clear remaining IDs if there are none
    onRemainingIdsUpdate(undefined);

    // Check for import errors
    if (reports.remainingIdsFile.error || reports.importOutputLogFile.error || reports.legacyImportFile.error) {
      return { type: "error", message: "Import error detected", severity: "error" as const };
    }
    // Check for deploy errors
    if (reports.deployOutputLogFile.error) {
      return { type: "error", message: "Deploy error detected", severity: "error" as const };
    }

    // Check if there are changes in the branch
    const hasChanges = diffData && (diffData.added.length > 0 || diffData.removed.length > 0 || diffData.updated.length > 0);

    if (!hasChanges) {
      if (reports.commitCount === 0) {
        return { type: "no-changes", message: "No changes in branch", severity: "info" as const };
      } else {
        return {
          type: "ready",
          message: `No new changes, but branch is ready to submit (${reports.commitCount} commit${
            reports.commitCount === 1 ? "" : "s"
          })`,
          severity: "success" as const,
        };
      }
    }

    // Everything looks good
    return {
      type: "ready",
      message: `Ready to commit (${reports.commitCount} commit${reports.commitCount === 1 ? "" : "s"})`,
      severity: "success" as const,
    };
  }, [branchInfo, isLoading, reports, diffData, onRemainingIdsUpdate]);

  const hasChanges = useMemo(() => {
    return diffData && (diffData.added.length > 0 || diffData.removed.length > 0 || diffData.updated.length > 0);
  }, [diffData]);

  return (
    <>
      <TabPanelHeader>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={createBranchButtonClickHandler}
          sx={{ marginRight: 2, height: 44 }}
        >
          Create Branch
        </Button>
        <TextField
          id="branch-selector"
          select
          label="Branch"
          value={selectedBranch ?? ""}
          onChange={(event) => setSelectedBranch(event.target.value)}
          sx={{
            background: "white",
            minWidth: "280px",
          }}
          variant="filled"
          size="small"
        >
          <MenuItem key={""} value={""} />
          {branches.map((branch) => (
            <MenuItem key={branch} value={branch}>
              {branch}
            </MenuItem>
          ))}
        </TextField>
        {selectedBranch && branchInfo?.state !== undefined && (
          <>
            <Typography variant="body1" component="div" sx={{ marginLeft: 2, marginRight: 1 }}>
              Branch state:
            </Typography>
            {branchChip}
          </>
        )}
        {selectedBranch && (
          <>
            <Button
              variant="contained"
              onClick={commitBranchButtonClickHandler}
              sx={{ marginLeft: 1, marginRight: 1, height: 44 }}
              disabled={!hasChanges || branchInfo?.state !== GameConfigState.DRAFT}
            >
              Commit
            </Button>
            <Button
              variant="contained"
              onClick={revertBranchButtonClickHandler}
              sx={{ marginRight: 1, height: 44 }}
              disabled={!hasChanges}
            >
              Revert
            </Button>
          </>
        )}
        {selectedBranch && branchInfo?.state !== undefined && (
          <>
            {((branchInfo.state === GameConfigState.APPROVED || branchInfo.state === GameConfigState.IN_REVIEW) && (
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={mergeBranchButtonClickHandler}
                sx={{ marginLeft: 1, marginRight: 1, height: 44 }}
                disabled={branchInfo.state !== GameConfigState.APPROVED}
              >
                Merge
              </Button>
            )) || (
              <Button
                variant="contained"
                startIcon={<KeyboardTabIcon />}
                onClick={submitBranchButtonClickHandler}
                sx={{ marginLeft: 1, marginRight: 1, height: 44 }}
                disabled={branchInfo.state !== GameConfigState.DRAFT || !reports?.commitCount || reports.commitCount === 0}
              >
                Submit
              </Button>
            )}
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={deleteBranchButtonClickHandler}
              sx={{ marginLeft: 1, height: 44 }}
              disabled={branchInfo.state !== GameConfigState.DRAFT}
            >
              Delete
            </Button>
          </>
        )}
      </TabPanelHeader>
      <div style={{ marginTop: 16 }}>
        {selectedBranch && (
          <>
            <Alert
              severity={branchStatus.severity}
              sx={{ mb: 2 }}
              action={
                branchStatus.type === "remaining-ids" ? (
                  <Button variant="outlined" color="inherit" size="small" onClick={onSwitchToImportTab}>
                    Fix Mappings
                  </Button>
                ) : undefined
              }
            >
              {branchStatus.message}
            </Alert>

            <Grid container spacing={2}>
              <Grid size={2}>
                <RadioGroup
                  value={selectedReportView}
                  onChange={(e) => setSelectedReportView(e.target.value as "import" | "deploy" | "legacy" | "changes")}
                >
                  <FormControlLabel value="import" control={<Radio />} label="Toolbox Import Output" />
                  <FormControlLabel value="deploy" control={<Radio />} label="Toolbox Deploy Output" />
                  <FormControlLabel value="legacy" control={<Radio />} label="Legacy Report" />
                  <FormControlLabel value="changes" control={<Radio />} label="Changes Summary" />
                </RadioGroup>
              </Grid>

              <Grid size={10}>
                <BranchStateReport reports={reports} diffData={diffData} selectedReportView={selectedReportView} />
              </Grid>
            </Grid>
          </>
        )}

        {!selectedBranch && (
          <Typography variant="subtitle1" color="textSecondary" sx={{ textAlign: "center", mt: 4 }}>
            Please select a branch to view reports.
          </Typography>
        )}
      </div>
    </>
  );
};
